import {first, isEmpty} from 'lodash';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {
  defineMessage,
  Trans,
} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {onlyItem} from '../../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {TagBehaviorsEnum} from '../../../../../../../libs/shared/core/src/models/api/commonTagModel';
import {TeamModel} from '../../../../../../../libs/shared/core/src/models/api/teamModel';
import {
  computeIsGlobalNamespace,
  isTeammateNamespace,
} from '../../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {
  tryFindInboxesPanelIdInPanelId,
  ViewerPanelId,
} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown, DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownBanner} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownBanner';
import {DropdownList} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownSeparator} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownSeparator';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {SimpleDropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {useLinkNavigation} from '../../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {ComplexTooltipContent} from '../../../../../../../libs/shared/ui/src/components/tooltips/complexTooltipContent';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import viewsTooltipImage from '../../../../../../assets/images/tagsViewsTooltipIllustration.png';
import viewsTooltipDarkModeImage from '../../../../../../assets/images/tagsViewsTooltipIllustrationDarkMode.png';
import {useAppDispatch} from '../../../../../../helpers/appReduxHelpers';
import {tagHasBehaviors} from '../../../../../../helpers/models/tagHelpers';
import {TagModel} from '../../../../../../models/api/tagModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../../../models/url/settingsUrlHelpers';
import {showCreateLensDialog} from '../../../../../../store/panels/inboxes/inboxesPanelActions';
import {helpCenterUrl} from '../../../../../../store/stateless/navigationActions';
import {LearnMoreButton} from '../../../../../common/callToAction/learnMoreButton';
import {PermissionDropdownItem} from '../../../../../common/dropdowns/permissionDropdownItem';
import {PermissionSimpleDropdownItem} from '../../../../../common/dropdowns/permissionSimpleDropdownItem';
import {LensUpdateHandler} from '../../../../workspaces/editor/context/workspacesEditorContext';
import {LensesWorkspaceCreateDialog} from '../../../../workspaces/lensesWorkspace/dialog/lensesWorkspaceCreateDialog';

/*
 * Props.
 */

interface TagDropdownProps extends DropdownProps {
  panelId: ViewerPanelId;
  tag: TagModel;
  isAddedToConversations: boolean;
  hasEditPermission: boolean;
  hasApplyPermission: boolean;
  hasVisibleCustomView: boolean;
  canPinTag: boolean;
  isSandboxTag: boolean;
  wasAddedByAi?: boolean;
  onNavigateToInboxClick: () => void;
  onViewConversationsClick: () => void;
  onEditClick: (event: FrontEvent) => void;
  onAddTagClick: (event: FrontEvent) => void;
  onUntagClick: (event: FrontEvent) => void;
  onShowClick: () => void;
  onHideClick: () => void;
  onShowInaccurateDialog: () => void;

  /*
   * Lens related.
   */

  teamsUserIsAdminOf: Array<TeamModel>;
  updateLensesWorkspace: LensUpdateHandler;
  teamNamespacesForSelectedConversations: ReadonlyArray<string>;
}

/*
 * Styles.
 */

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const tooltipImageStyle: React.CSSProperties = {
  // add negative margins to compensate for image drop shadow
  margin: '0 -9px',
  width: 'calc(100% + 9px)',
};

/*
 * Component.
 */

export const TagDropdown: FC<TagDropdownProps> = (props) => {
  const {
    hasVisibleCustomView,
    tag,
    canPinTag,
    isSandboxTag,
    wasAddedByAi,
    hasApplyPermission,
    teamsUserIsAdminOf,
    teamNamespacesForSelectedConversations,
    onShowInaccurateDialog,
  } = props;
  const {isMainInboxTag, archivedAt} = tag;
  const isCompanyTag = computeIsGlobalNamespace(tag.namespace);
  const isTeammateTag = isTeammateNamespace(tag.namespace);

  const dispatch = useAppDispatch();
  const localize = useLocalize();
  const {navigateToLink} = useLinkNavigation();

  const [isCreateLensDialogOpen, setIsCreateLensDialogOpen] = useState(false);
  const inboxesPanelId = useMemo(() => tryFindInboxesPanelIdInPanelId(props.panelId), [props.panelId]);

  const commonTeamNamespaceForSelectedConversations = onlyItem(teamNamespacesForSelectedConversations);

  const initialLensNamespace = isCompanyTag
    ? // Try to find common namespace for selected conversations
      commonTeamNamespaceForSelectedConversations ||
      // Try to use the first team namespace for selected conversations
      first(teamNamespacesForSelectedConversations) ||
      // Use the first team namespace the user is admin of
      first(teamsUserIsAdminOf)?.namespace
    : tag.namespace;

  const canCreatePrivateView = !isMainInboxTag && !isTeammateTag && !isSandboxTag;
  const canCreateSharedView = canCreatePrivateView && !isEmpty(teamsUserIsAdminOf) && initialLensNamespace;

  /*
   * Event handlers.
   */

  const onCreatePrivateViewClick = (event: FrontEvent) => {
    if (inboxesPanelId) {
      dispatch(showCreateLensDialog(inboxesPanelId, initialLensNamespace, [tag.id]));
      return;
    }

    event.preventDefault();
    // If there isn't an inboxes panel id, open the dialog within the popout.
    setIsCreateLensDialogOpen(true);
  };

  const onCreateSharedViewClick = () => {
    if (!canCreateSharedView) {
      return;
    }

    navigateToLink(
      buildSettingsPath({
        lensId: undefined,
        namespace: initialLensNamespace,
        rootPage: SettingsRootPagesEnum.LENSES,
        resourcePage: SettingsResourcePagesEnum.NEW,
        tagIdsFilter: [tag.id],
      }),
    );
  };

  /*
   * Render.
   */

  return (
    <>
      <Dropdown {...props} maxWidth={220}>
        <DropdownList {...props}>
          {archivedAt && (
            <DropdownBanner>
              <Trans id="tag-details-archived">This tag is archived</Trans>
            </DropdownBanner>
          )}
          {isMainInboxTag ? (
            <SimpleDropdownItem onSelect={props.onNavigateToInboxClick}>
              <Trans id="tag-details-gotoinbox">Go to Inbox</Trans>
            </SimpleDropdownItem>
          ) : (
            // TODO: Pass permission prop for access check instead of getHasEditPermission selector.
            <PermissionDropdownItem onSelect={props.onEditClick} isDisabled={!props.hasEditPermission}>
              <DropdownItemContent>
                <Trans id="tag-details-edit">Edit</Trans>
              </DropdownItemContent>
            </PermissionDropdownItem>
          )}
          {renderAddRemoveItem(props)}
          {!isMainInboxTag && (
            <>
              <DropdownSeparator />
              <SimpleDropdownItem onSelect={props.onViewConversationsClick}>
                <Trans id="tag-details-viewconversations">View conversations</Trans>
              </SimpleDropdownItem>
            </>
          )}
          {canCreatePrivateView && (
            <>
              <DropdownSeparator />
              <PermissionDropdownItem onSelect={onCreatePrivateViewClick}>
                <StyledDropdownItemContent>
                  <Trans id="lenses-workspace-create-lens">Create private view</Trans>
                  {!canCreateSharedView && renderViewsTooltip(false, localize)}
                </StyledDropdownItemContent>
              </PermissionDropdownItem>
              {canCreateSharedView && (
                <PermissionDropdownItem onSelect={onCreateSharedViewClick}>
                  <StyledDropdownItemContent>
                    <Trans id="lenses-workspace-create-lens">Create shared view</Trans>
                    {renderViewsTooltip(true, localize)}
                  </StyledDropdownItemContent>
                </PermissionDropdownItem>
              )}
            </>
          )}
          {canPinTag && !isMainInboxTag && !archivedAt && (
            <>
              <DropdownSeparator />
              {hasVisibleCustomView ? (
                <PermissionSimpleDropdownItem onSelect={props.onHideClick}>
                  <Trans id="tag-details-hideinsidebar">Hide in sidebar</Trans>
                </PermissionSimpleDropdownItem>
              ) : (
                <PermissionSimpleDropdownItem onSelect={props.onShowClick}>
                  <Trans id="tag-details-showinsidebar">Show in sidebar</Trans>
                </PermissionSimpleDropdownItem>
              )}
            </>
          )}
          {maybeRenderReportInaccurateTag(wasAddedByAi, hasApplyPermission, onShowInaccurateDialog)}
        </DropdownList>
      </Dropdown>
      {isCreateLensDialogOpen && (
        <LensesWorkspaceCreateDialog
          panelId={inboxesPanelId}
          workspaceUpdateHandler={props.updateLensesWorkspace}
          initialNamespace={initialLensNamespace}
          initialTagIdsFilter={[tag.id]}
          onRequestClose={() => setIsCreateLensDialogOpen(false)}
        />
      )}
    </>
  );
};

function renderViewsTooltip(canCreateSharedView: boolean, localize: Localizer) {
  return (
    <TooltipCoordinator
      isInline
      persistent
      placement="right-start"
      render={() => (
        <ComplexTooltipContent
          title={tooltipTitle}
          body={
            canCreateSharedView ? (
              <Trans id="tags-views-tooltip-body-admin">
                Enable your team to monitor important conversations from their sidebar using criteria like
                inboxes, tags, assignees or custom fields.
              </Trans>
            ) : (
              <Trans id="tags-views-tooltip-body-flu">
                Monitor important conversations from your sidebar using criteria like inboxes, tags, assignees
                or custom fields.
              </Trans>
            )
          }
          imageStyle={tooltipImageStyle}
          imageSrc={viewsTooltipImage}
          darkModeImageSrc={viewsTooltipDarkModeImage}
          imageDescription={localize(imageAltMessage)}
          buttons={tooltipLearnMoreButton}
        />
      )}
      borderRadius={16}
    />
  );
}

const tooltipTitle = <Trans id="tags-views-tooltip-admin-title">Views</Trans>;

const imageAltMessage = defineMessage({
  id: 'workspaces-views-tooltip-image',
  message: 'image of views in sidebar tooltip',
});

const tooltipLearnMoreButton = (
  <LearnMoreButton
    variant={ButtonVariantsEnum.TOOLTIP_LINK}
    helpPageUrl={`${helpCenterUrl}/en/articles/2243`}
  />
);

/*
 * Helpers.
 */

function renderAddRemoveItem(props: TagDropdownProps) {
  const {tag, isAddedToConversations, hasApplyPermission, onAddTagClick, onUntagClick} = props;

  if (!tagHasBehaviors(tag, TagBehaviorsEnum.CAN_APPLY_FROM_LIST)) {
    return null;
  }

  if (isAddedToConversations) {
    return (
      <PermissionSimpleDropdownItem onSelect={onUntagClick} isDisabled={!hasApplyPermission}>
        <Trans id="tag-details-remove">Remove from conversation</Trans>
      </PermissionSimpleDropdownItem>
    );
  }

  return (
    <PermissionSimpleDropdownItem onSelect={onAddTagClick} isDisabled={!hasApplyPermission}>
      <Trans id="tag-details-add">Add to conversation</Trans>
    </PermissionSimpleDropdownItem>
  );
}

function maybeRenderReportInaccurateTag(
  wasAddedByAi: boolean | undefined,
  hasApplyPermission: boolean,
  onSelect: () => void,
) {
  if (!wasAddedByAi) {
    return null;
  }
  return (
    <>
      <DropdownSeparator />
      <PermissionSimpleDropdownItem onSelect={onSelect} isDisabled={!hasApplyPermission}>
        <Trans id="tag-details-report-inaccurate-tag">Report inaccurate tag</Trans>
      </PermissionSimpleDropdownItem>
    </>
  );
}
